/**
 * The "Supported languages and frameworks" page (pages/SupportedStacks.jsx) and the in-context notes: the columns of
 * the matrix (vfs-service GET /api/v1/stacks, services/StacksService.js), Yes / Partly / No, sorting and filtering,
 * and the link to the page.
 */

export const STACKS_PATH = '/supported-stacks';

/** The page's URL with the app's base path (the links' href; a click opens the dialog); `stack`: the row to point at. */
export function stacksHref(stack = null) {
  const basePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  return `${basePath}${STACKS_PATH}${stack ? `?stack=${encodeURIComponent(stack)}` : ''}`;
}

export const STATUS = {
  yes: { label: 'Yes', tone: 'yes', rank: 0 },
  partial: { label: 'Partly', tone: 'partial', rank: 1 },
  no: { label: 'No', tone: 'no', rank: 2 },
};

export const LEGEND = [
  { status: 'yes', text: 'Flow does it on its own.' },
  { status: 'partial', text: 'It works with a choice or an addition of yours, or only for some tools (see the note).' },
  { status: 'no', text: 'Not supported yet.' },
];

/** The capabilities, in the order of the table; `key` is the field of a stack. */
export const CAPABILITIES = [
  { key: 'run', label: 'Runs' },
  { key: 'preview', label: 'Preview' },
  { key: 'tests', label: 'Tests' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'lint', label: 'Lint' },
  { key: 'lockfile', label: 'Lockfile' },
  { key: 'vulnerabilities', label: 'Vulnerabilities' },
  { key: 'licences', label: 'Licences' },
  { key: 'sbom', label: 'SBOM' },
];

/** The table's columns: the stack (always shown), its family, the capabilities, the person's plan. */
export const COLUMNS = [
  { key: 'label', label: 'Language or framework' },
  { key: 'family', label: 'Family' },
  ...CAPABILITIES,
  { key: 'plan', label: 'Your plan' },
];

export const statusOf = (stack, key) => (stack && stack[key] && STATUS[stack[key].status] ? stack[key].status : 'no');
export const allowedOf = (stack) => !stack?.available || stack.available.allowed !== false;

/** The plan cell: Allowed, or why not (`plan`: not in your plan; `cluster`: this Flow has no Docker). */
export function planCell(stack) {
  if (allowedOf(stack)) { return { allowed: true, label: 'Allowed', reason: null, message: null }; }
  const { reason, message } = stack.available;
  return { allowed: false, label: reason === 'cluster' ? 'Not in this Flow' : 'Not in your plan', reason, message };
}

/** The value a column sorts by. */
function sortValue(stack, key, order) {
  if (key === 'label') { return String(stack.label || '').toLowerCase(); }
  if (key === 'family') { return order.indexOf(stack.family) === -1 ? 999 : order.indexOf(stack.family); }
  if (key === 'plan') { return allowedOf(stack) ? 0 : 1; }
  return STATUS[statusOf(stack, key)].rank;
}

/** The stacks sorted by `key` (`dir` asc | desc), ties in the matrix's order; no key: as they came. */
export function sortStacks(stacks, key, dir = 'asc', families = []) {
  if (!key) { return stacks; }
  const sign = dir === 'desc' ? -1 : 1;
  return stacks
    .map((stack, index) => ({ stack, index }))
    .sort((left, right) => {
      const a = sortValue(left.stack, key, families);
      const b = sortValue(right.stack, key, families);
      if (a < b) { return -sign; }
      if (a > b) { return sign; }
      return left.index - right.index;
    })
    .map(({ stack }) => stack);
}

/** The stacks matching a search (label, family, what detects it, the frameworks named) and a family (`all`). */
export function filterStacks(stacks, { search = '', family = 'all', allowedOnly = false } = {}) {
  const words = String(search).trim().toLowerCase().split(/\s+/).filter(Boolean);
  return stacks.filter((stack) => {
    if (family !== 'all' && stack.family !== family) { return false; }
    if (allowedOnly && !allowedOf(stack)) { return false; }
    if (!words.length) { return true; }
    const text = [stack.label, stack.family, stack.id, ...(stack.detectedBy || []), ...CAPABILITIES.map((c) => stack[c.key]?.summary || '')].join(' ').toLowerCase();
    return words.every((word) => text.includes(word));
  });
}

/** The families in the matrix's order, then any other the stacks name. */
export function familiesOf(answer) {
  const listed = answer?.families || [];
  const named = (answer?.stacks || []).map((stack) => stack.family);
  return [...new Set([...listed.filter((family) => named.includes(family)), ...named])];
}

/**
 * What Flow does not do for a service's stack, for an in-context note: `{ stack, label, missing: [capability label] }`
 * of `keys`, or null when it does them all (or the stack is unknown).
 */
export function gapsFor(answer, stackId, keys = CAPABILITIES.map((c) => c.key)) {
  const stack = (answer?.stacks || []).find((item) => item.id === stackId);
  if (!stack) { return null; }
  const missing = CAPABILITIES.filter((c) => keys.includes(c.key) && statusOf(stack, c.key) === 'no').map((c) => c.label);
  return missing.length ? { stack: stack.id, label: stack.label, missing } : null;
}
